/**
 * Theme Registry and Exports
 * 
 * Central export point for all themes and theme-related utilities.
 * This module provides the theme registry, helper functions, and type exports
 * for the theming system.
 * 
 * @module themes
 */

import { retroTheme } from './retro';
import { modernTheme } from './modern';
import type { Theme, ThemeName } from './types';

/**
 * Theme registry
 * 
 * Maps theme names to their corresponding theme objects.
 * This is the single source of truth for all available themes.
 * 
 * @constant
 * @type {Record<ThemeName, Theme>}
 * 
 * @example
 * ```typescript
 * import { themes } from './themes';
 * 
 * const retroTheme = themes.retro;
 * const modernTheme = themes.modern;
 * ```
 */
export const themes: Record<ThemeName, Theme> = {
  retro: retroTheme,
  modern: modernTheme,
};

/**
 * Get theme by name
 * 
 * Returns the theme object for the given theme name.
 * Provides type-safe access to theme objects.
 * 
 * @function getTheme
 * @param {ThemeName} themeName - The name of the theme to retrieve
 * @returns {Theme} The theme object
 * 
 * @example
 * ```typescript
 * const theme = getTheme('modern');
 * console.log(theme.colors.bg); // '#0a0e27'
 * ```
 */
export function getTheme(themeName: ThemeName): Theme {
  return themes[themeName];
}

/**
 * Validate theme name
 * 
 * Type guard function that checks if a given string is a valid theme name.
 * Useful for validating user input or data from external sources.
 * 
 * @function isValidThemeName
 * @param {string} name - The name to validate
 * @returns {boolean} True if the name is a valid theme name
 * 
 * @example
 * ```typescript
 * if (isValidThemeName(userInput)) {
 *   setTheme(userInput); // TypeScript knows userInput is ThemeName
 * }
 * ```
 */
export function isValidThemeName(name: string): name is ThemeName {
  return name === 'retro' || name === 'modern';
}

/**
 * Get all available theme names
 * 
 * Returns an array of all supported theme names.
 * Useful for generating theme selection UI or iterating over themes.
 * 
 * @function getThemeNames
 * @returns {ThemeName[]} Array of theme names
 * 
 * @example
 * ```typescript
 * const themeNames = getThemeNames(); // ['retro', 'modern']
 * 
 * // Generate theme selector
 * themeNames.map(name => (
 *   <option key={name} value={name}>{name}</option>
 * ))
 * ```
 */
export function getThemeNames(): ThemeName[] {
  return Object.keys(themes) as ThemeName[];
}

// Re-export types for convenience
export type {
  Theme,
  ThemeName,
  ThemeColors,
  ThemeTypography,
  ThemeSpacing,
  ThemeBorderRadius,
  ThemeShadows,
  ThemeTransitions,
  ThemeContextValue,
} from './types';

// Re-export theme objects
export { retroTheme } from './retro';
export { modernTheme } from './modern';
